import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Clock,
  AlertTriangle,
  Calculator,
  Target,
  Shield,
  TrendingUp,
} from 'lucide-react'

export const Route = createFileRoute('/_site/blog/what-is-uptime-sla')({
  component: WhatIsUptimeSlaPage,
  head: () => ({
    meta: [
      {
        title:
          'What Is a Good Uptime SLA? 99.9% vs 99.99% — Uptrack',
      },
      {
        name: 'description',
        content:
          '99.9% uptime allows 8.7 hours of downtime a year. The difference between 99.9% and 99.99% SLAs, how to calculate them, and why check interval matters.',
      },
      {
        property: 'og:title',
        content: 'What Is a Good Uptime SLA? 99.9% vs 99.99% — Uptrack',
      },
      {
        property: 'og:description',
        content:
          '99.9% uptime means 8.7 hours of downtime per year. Here\'s what every SLA percentage actually means and how monitoring helps you hit it.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/blog-what-is-uptime-sla.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/blog/what-is-uptime-sla',
      },
      { property: 'article:published_time', content: '2026-04-01' },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/blog/what-is-uptime-sla',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline:
            'What Is a Good Uptime SLA? 99.9% vs 99.99% Explained',
          description:
            'What 99.9% uptime actually means in real downtime minutes, how to calculate SLA, and how monitoring interval affects detection.',
          datePublished: '2026-04-01',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/blog/what-is-uptime-sla',
        }),
      },
    ],
  }),
})

const slaTable = [
  { sla: '99%', yearly: '3.65 days', monthly: '7.3 hours', weekly: '1.68 hours' },
  { sla: '99.5%', yearly: '1.83 days', monthly: '3.65 hours', weekly: '50.4 min' },
  { sla: '99.9%', yearly: '8.77 hours', monthly: '43.8 min', weekly: '10.1 min' },
  { sla: '99.95%', yearly: '4.38 hours', monthly: '21.9 min', weekly: '5.04 min' },
  { sla: '99.99%', yearly: '52.6 min', monthly: '4.38 min', weekly: '1.01 min' },
  { sla: '99.999%', yearly: '5.26 min', monthly: '26.3 sec', weekly: '6.05 sec' },
]

function WhatIsUptimeSlaPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-3 text-sm text-muted-foreground">April 1, 2026</p>
          <h1 className="font-sans mb-6 text-4xl font-semibold">
            What is a good uptime SLA? 99.9% vs 99.99% explained
          </h1>

          {/* Intro */}
          <section className="mb-12">
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              "We guarantee 99.9% uptime." Sounds impressive, right? That's
              almost 100%. Surely nothing bad can happen in that 0.1%.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              Except 0.1% of a year is 8 hours and 46 minutes. That's an
              entire workday where your service could be completely
              unreachable, and you'd still be within SLA. When a customer
              asks "what's your uptime guarantee?" they hear "your service
              will basically always be up." What the number actually means
              is very different. New to the topic? Start with{' '}
              <Link to="/blog/what-is-uptime-monitoring" className="underline">what uptime monitoring is</Link>.
            </p>
          </section>

          {/* The nines table */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              The nines: what each SLA actually means
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Each additional "nine" in an uptime SLA represents a 10x
              reduction in allowed downtime. The jump from 99.9% to 99.99%
              isn't a small improvement. It's the difference between "we
              can go down for a few minutes every month" and "we can barely
              go down at all."
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">
                      SLA
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Downtime / year
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Downtime / month
                    </th>
                    <th className="pb-3 pl-3 text-center font-medium text-muted-foreground">
                      Downtime / week
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {slaTable.map((row) => (
                    <tr
                      key={row.sla}
                      className={`border-b last:border-0 ${row.sla === '99.9%' || row.sla === '99.99%' ? 'bg-muted' : ''}`}
                    >
                      <td className="py-3 pr-4 num font-medium">
                        {row.sla}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {row.yearly}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {row.monthly}
                      </td>
                      <td className="py-3 pl-3 text-center text-muted-foreground">
                        {row.weekly}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-lg leading-[1.7] text-body">
              Most SaaS companies offer 99.9% (three nines). Major cloud
              providers like AWS and Google Cloud offer 99.95% to 99.99% on
              core services. Five nines (99.999%) is reserved for systems
              like telephone networks and financial infrastructure.
            </p>
          </section>

          {/* How to calculate */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How to calculate your uptime SLA
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              The formula is straightforward. Track total time and subtract
              downtime.
            </p>
            <div className="rounded-lg border border-border bg-card p-5 mb-6">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                SLA formula
              </p>
              <pre tabIndex={0} className="overflow-x-auto text-sm leading-relaxed">
                <code className="text-muted-foreground">{`Uptime % = ((Total minutes - Downtime minutes) / Total minutes) * 100

Example (one month):
  Total minutes:    43,200 (30 days)
  Downtime minutes: 45
  Uptime %:         ((43200 - 45) / 43200) * 100 = 99.896%

  Verdict: Missed 99.9% SLA by 1.8 minutes`}</code>
              </pre>
            </div>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              The tricky part is defining "downtime." Some SLAs exclude
              scheduled maintenance windows, only count downtime during
              business hours, or require the customer to report the outage
              before the clock starts. Always read the fine print.
            </p>
            <div className="rounded-lg border border-status-degraded/30 bg-status-degraded/5 p-4">
              <p className="text-lg leading-[1.7] text-body">
                <span className="font-semibold text-status-degraded">
                  Watch out:
                </span>{' '}
                Some providers calculate SLA based on "successful requests"
                instead of time-based availability. If 1 out of every 1,000
                requests fails, that's 99.9% by their math, even if those
                failures happen in a concentrated 2-hour burst. Time-based
                SLAs are more honest.
              </p>
            </div>
          </section>

          {/* Which SLA do you need */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Which SLA does your product need?
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              The answer depends on what happens when you go down. Not every
              product needs five nines. Most don't need four.
            </p>

            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">99.9% — Most SaaS products</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  If your product is a project management tool, CRM, analytics
                  dashboard, or content platform, 99.9% is the industry
                  standard. Your customers expect the product to work, but
                  occasional brief outages are tolerable. You'll budget for
                  about 44 minutes of downtime per month.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">99.95% — Business-critical SaaS</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  If customers depend on your product for real-time operations
                  (communication tools, e-commerce platforms, booking systems),
                  you need a tighter target. 99.95% gives you about 22 minutes
                  per month. Slack, for reference, targets this range.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-primary" />
                  <h3 className="font-sans font-semibold">99.99% — Infrastructure and payments</h3>
                </div>
                <p className="text-lg leading-[1.7] text-body">
                  Payment processing, authentication services, and
                  infrastructure APIs need four nines. Downtime means your
                  customers' customers are affected. At 99.99%, you get about
                  4 minutes of downtime per month. This requires redundancy
                  at every layer: multi-region, automated failover, zero-
                  downtime deployments.
                </p>
              </div>
            </div>
          </section>

          {/* Monitoring interval matters */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How monitoring interval affects SLA tracking
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Your monitoring check interval directly impacts how accurately
              you can measure and respond to downtime. If you check every 5
              minutes, your downtime measurement has a 5-minute margin of
              error in both directions.
            </p>

            <div className="grid gap-4 sm:grid-cols-2 mb-6">
              <div className="rounded-lg border border-border bg-card p-5">
                <Calculator className="mb-3 h-6 w-6 text-status-down" />
                <h3 className="font-sans mb-2 font-semibold">5-minute checks</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Your site goes down at 14:01. Your monitor last checked at
                  14:00 (passed). The next check at 14:05 detects the failure.
                  You're alerted 4 minutes after the outage started. If you
                  fix it at 14:08, the next passing check is at 14:10. Your
                  measured downtime is 10 minutes, but actual downtime was 7
                  minutes.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Clock className="mb-3 h-6 w-6 text-status-up" />
                <h3 className="font-sans mb-2 font-semibold">30-second checks</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Same scenario. Your site goes down at 14:01. Your monitor
                  checks at 14:01 or 14:02 and detects the failure immediately.
                  You're alerted within 60 seconds. You fix it at 14:08, and
                  the next passing check confirms at 14:08 or 14:09. Your
                  measured downtime is accurate to within 1 minute.
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted p-5">
              <p className="text-lg leading-[1.7] text-body">
                <span className="font-semibold text-primary">
                  The math:
                </span>{' '}
                With 30-second checks on Uptrack, you detect
                outages in under 60 seconds. With UptimeRobot's free plan
                (5-minute checks), the same outage takes up to 5 minutes to
                detect. That's the difference between a 99.9% SLA with 43
                minutes of allowed monthly downtime and burning through 11%
                of your budget before you even know something is wrong.
              </p>
            </div>
          </section>

          {/* How to actually hit your SLA */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              How to actually hit your uptime target
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              An SLA is a promise. Monitoring is how you keep it. Here are the
              engineering practices that separate teams who hit their SLA from
              teams who pay refunds.
            </p>
            <div className="space-y-4">
              {[
                {
                  title: 'Deploy with zero-downtime strategies',
                  description:
                    'Rolling deployments, blue-green, or canary releases. Every deployment that takes your site down for 30 seconds chips away at your SLA budget. At 99.99%, you only get 4.38 minutes per month total.',
                },
                {
                  title: 'Monitor faster than your SLA requires',
                  description:
                    'If you promise 99.9% uptime (43 minutes/month of allowed downtime), you cannot afford to detect issues with 5-minute checks. By the time you detect and fix, half your budget is gone. Use 1-minute or faster checks.',
                },
                {
                  title: 'Automate incident response',
                  description:
                    'The fastest fix is the one that happens without human intervention. Auto-restart crashed processes, auto-scale on load spikes, auto-failover to standby databases. Human response time is the bottleneck.',
                },
                {
                  title: 'Track your SLA budget in real time',
                  description:
                    'Know how much downtime you have left this month. If you\'ve already used 30 of your 43 allowed minutes, postpone that risky deployment until next month.',
                },
                {
                  title: 'Use consecutive-check confirmation',
                  description:
                    'False alerts waste time. If your team spends 15 minutes investigating a false alarm, that\'s 15 minutes they\'re not shipping. Consecutive-check confirmation ensures you only respond to real issues.',
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <TrendingUp className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <span className="font-semibold">{item.title}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      -- {item.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Does scheduled maintenance count against SLA?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  It depends on the SLA agreement. Most SaaS providers exclude
                  pre-announced maintenance windows from SLA calculations. But
                  your customers still experience downtime during maintenance.
                  The best approach is zero-downtime deployments so maintenance
                  windows become irrelevant.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  What happens when you breach your SLA?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Typically, SLA breaches result in service credits — a
                  percentage of the customer's bill credited back. AWS, for
                  example, offers 10% credit for availability below 99.99%
                  and 30% below 99.0%. The financial penalty incentivizes
                  providers to invest in reliability.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">
                  Can I offer 99.99% uptime on a single server?
                </h3>
                <p className="text-lg leading-[1.7] text-body">
                  Realistically, no. A single server means any hardware
                  failure, kernel update, or network issue takes you down
                  completely. Four nines requires redundancy: multiple
                  servers, load balancing, database replication, and
                  automated failover. A single server can reasonably
                  achieve 99.5% to 99.9%.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Clock className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Track Your Uptime SLA
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              All monitors at 30-second checks, from $3/mo.
              30-day money-back guarantee.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
